import * as THREE from 'three';

/** Shared uniform driving all wind-sway shaders. Updated once per frame. */
export const windTime = { value: 0 };

let gradientMap: THREE.DataTexture | null = null;

/** Three-tone ramp for the cel-shaded look. */
export function toonGradient(): THREE.DataTexture {
  if (!gradientMap) {
    const data = new Uint8Array([120, 200, 255]);
    gradientMap = new THREE.DataTexture(data, data.length, 1, THREE.RedFormat);
    gradientMap.minFilter = THREE.NearestFilter;
    gradientMap.magFilter = THREE.NearestFilter;
    gradientMap.generateMipmaps = false;
    gradientMap.needsUpdate = true;
  }
  return gradientMap;
}

const toonCache = new Map<string, THREE.MeshToonMaterial>();

/** Cached cel-shaded material for a flat colour. Do not mutate the result. */
export function toon(color: THREE.ColorRepresentation): THREE.MeshToonMaterial {
  const key = new THREE.Color(color).getHexString();
  let material = toonCache.get(key);
  if (!material) {
    material = new THREE.MeshToonMaterial({ color, gradientMap: toonGradient() });
    toonCache.set(key, material);
  }
  return material;
}

/** A fresh (uncached) toon material, for things that animate their colour. */
export function toonUnique(params: THREE.MeshToonMaterialParameters = {}): THREE.MeshToonMaterial {
  return new THREE.MeshToonMaterial({ gradientMap: toonGradient(), ...params });
}

/**
 * Injects a gentle wind sway into a built-in material. Vertices are displaced
 * proportionally to the square of their local height, so bases stay planted.
 */
export function addWind<T extends THREE.Material>(material: T, strength: number, key: string): T {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uWindTime = windTime;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uWindTime;')
      .replace(
        '#include <begin_vertex>',
        /* glsl */ `#include <begin_vertex>
        {
          #ifdef USE_INSTANCING
            vec4 windOrigin = modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          #else
            vec4 windOrigin = modelMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          #endif
          float windH = max(transformed.y, 0.0);
          float windPhase = uWindTime * 1.6 + windOrigin.x * 0.35 + windOrigin.z * 0.27;
          float windSway = (sin(windPhase) * 0.6 + sin(windPhase * 2.3 + 1.7) * 0.25 + 0.25)
            * ${strength.toFixed(4)} * windH * windH;
          transformed.x += windSway;
          transformed.z += windSway * 0.35;
        }`,
      );
  };
  material.customProgramCacheKey = () => `wind-${key}`;
  return material;
}

/** Soft round sprite texture used by particles, fireflies and glows. */
export function makeGlowTexture(size = 64): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.4, 'rgba(255,255,255,0.6)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}
